import { memo, useEffect, useId, useMemo } from "react"
import { Controller, type SubmitHandler, useForm } from "react-hook-form"
import type { Resolver } from "react-hook-form"

import {
  Box,
  Button,
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPositioner,
  DialogRoot,
  DialogTitle,
  HStack,
  Input,
  Portal,
  Switch,
  Textarea,
} from "@chakra-ui/react"

import { Field } from "@/components/atoms/Field"
import ForeignKeySelect from "@/components/molecules/ForeignKeySelect"
import { useClassSessions } from "@/hooks/api/useClassSessions"
import { useEnrollments } from "@/hooks/api/useEnrollments"
import { useStudents } from "@/hooks/api/useStudents"
import type { AttendanceUpdateInput } from "@/types/attendance"

const skipLinkStyle = {
  position: "absolute",
  left: "-9999px",
  top: "-9999px",
  background: "#3182ce",
  color: "white",
  padding: "8px 16px",
  textDecoration: "none",
  borderRadius: "6px",
  zIndex: 1000,
} as const

type AttendanceFormValues = AttendanceUpdateInput

interface AttendanceFormDialogProps {
  isOpen: boolean
  title: string
  submitLabel: string
  resolver: Resolver<AttendanceFormValues>
  defaultValues: AttendanceFormValues
  onSubmit: (values: AttendanceFormValues) => Promise<void>
  onClose: () => void
  isSubmitting?: boolean
  cancelLabel?: string
  finalFocusRef?: React.RefObject<HTMLElement | null>
  showSkipLink?: boolean
  loadingText?: string
}

const AttendanceFormDialog = memo(
  ({
    isOpen,
    title,
    submitLabel,
    resolver,
    defaultValues,
    onSubmit,
    onClose,
    isSubmitting = false,
    cancelLabel = "Cancel",
    finalFocusRef,
    showSkipLink = false,
    loadingText,
  }: AttendanceFormDialogProps) => {
    const formSectionId = useId()

    // Backend already filters data by user permissions!
    // Parents automatically get only their household's students/enrollments
    const { data: studentsData, isLoading: studentsLoading } = useStudents()
    const { data: enrollmentsData, isLoading: enrollmentsLoading } =
      useEnrollments()
    const { data: classSessionsData, isLoading: classSessionsLoading } =
      useClassSessions()

    const normalizedDefaults = useMemo<AttendanceFormValues>(
      () => ({
        enrollment_id: defaultValues.enrollment_id ?? "",
        class_session_id: defaultValues.class_session_id ?? "",
        student_id: defaultValues.student_id ?? "",
        class_date: defaultValues.class_date ?? "",
        class_time: defaultValues.class_time ?? null,
        status: defaultValues.status ?? null,
        check_in_time: defaultValues.check_in_time ?? null,
        check_out_time: defaultValues.check_out_time ?? null,
        is_makeup_class: defaultValues.is_makeup_class ?? false,
        makeup_for_date: defaultValues.makeup_for_date ?? null,
        instructor_notes: defaultValues.instructor_notes ?? null,
        skills_practiced: defaultValues.skills_practiced ?? null,
        behavior_notes: defaultValues.behavior_notes ?? null,
        parent_notification_sent:
          defaultValues.parent_notification_sent ?? false,
        marked_by_user_id: defaultValues.marked_by_user_id ?? null,
        notes: defaultValues.notes ?? null,
      }),
      [defaultValues],
    )

    const form = useForm<AttendanceFormValues>({
      resolver,
      mode: "onBlur",
      defaultValues: normalizedDefaults,
    })

    useEffect(() => {
      if (isOpen) {
        void form.reset(normalizedDefaults)
      }
    }, [form, isOpen, normalizedDefaults])

    const handleClose = () => {
      form.reset(normalizedDefaults)
      onClose()
    }

    const handleSubmit: SubmitHandler<AttendanceFormValues> = async (
      values,
    ) => {
      try {
        await onSubmit(values)
        handleClose()
      } catch (error) {
        // Error handling is delegated to the mutation layer
      }
    }

    return (
      <DialogRoot
        open={isOpen}
        onOpenChange={(details) => {
          if (!details.open) {
            handleClose()
          }
        }}
        size={{ base: "full", md: "xl" }}
        trapFocus
        restoreFocus
        initialFocusEl={() => null}
        finalFocusEl={() => finalFocusRef?.current ?? null}
      >
        <Portal>
          <DialogBackdrop />
          <DialogPositioner
            inset="0"
            display="grid"
            placeItems="center"
            padding={{ base: 4, md: 6 }}
          >
            <DialogContent as="form" onSubmit={form.handleSubmit(handleSubmit)} maxH="90vh" display="flex" flexDirection="column">
              {showSkipLink && (
                <a
                  href={`#${formSectionId}`}
                  style={skipLinkStyle}
                  onFocus={(event) => {
                    event.currentTarget.style.left = "16px"
                    event.currentTarget.style.top = "16px"
                  }}
                  onBlur={(event) => {
                    event.currentTarget.style.left = "-9999px"
                    event.currentTarget.style.top = "-9999px"
                  }}
                >
                  Skip to form fields
                </a>
              )}

              <DialogHeader>
                <DialogTitle>{title}</DialogTitle>
                <DialogCloseTrigger />
              </DialogHeader>

              <DialogBody id={formSectionId} overflowY="auto" flex="1">
                <Box display="grid" gridTemplateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={6}>
                  <Field
                    required
                    invalid={!!form.formState.errors.enrollment_id}
                    errorText={
                      form.formState.errors.enrollment_id?.message as
                        | string
                        | undefined
                    }
                    label="Enrollment"
                  >
                    <ForeignKeySelect
                      register={form.register("enrollment_id")}
                      options={enrollmentsData?.items}
                      isLoading={enrollmentsLoading}
                      placeholder="Select Enrollment"
                      hasError={!!form.formState.errors.enrollment_id}
                      formatLabel={(e) =>
                        `Enrollment #${e.id.slice(0, 8)} - ${e.status}`
                      }
                    />
                  </Field>

                  <Field
                    required
                    invalid={!!form.formState.errors.class_session_id}
                    errorText={
                      form.formState.errors.class_session_id?.message as
                        | string
                        | undefined
                    }
                    label="Class Session"
                  >
                    <ForeignKeySelect
                      register={form.register("class_session_id")}
                      options={classSessionsData?.items}
                      isLoading={classSessionsLoading}
                      placeholder="Select Class Session"
                      hasError={!!form.formState.errors.class_session_id}
                      formatLabel={(m) =>
                        `${m.session_date} at ${m.actual_start_time || "TBD"}`
                      }
                    />
                  </Field>

                  <Field
                    required
                    invalid={!!form.formState.errors.student_id}
                    errorText={
                      form.formState.errors.student_id?.message as
                        | string
                        | undefined
                    }
                    label="Student"
                  >
                    <ForeignKeySelect
                      register={form.register("student_id")}
                      options={studentsData?.items}
                      isLoading={studentsLoading}
                      placeholder="Select Student"
                      hasError={!!form.formState.errors.student_id}
                      formatLabel={(s) => `${s.first_name} ${s.last_name}`}
                    />
                  </Field>

                  <Field
                    required
                    invalid={!!form.formState.errors.class_date}
                    errorText={
                      form.formState.errors.class_date?.message as
                        | string
                        | undefined
                    }
                    label="Class Date"
                  >
                    <Input
                      type="date"
                      placeholder={"Select class date"}
                      aria-invalid={!!form.formState.errors.class_date}
                      {...form.register("class_date")}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.class_time}
                    errorText={
                      form.formState.errors.class_time?.message as
                        | string
                        | undefined
                    }
                    label="Class Time"
                  >
                    <Input
                      placeholder={"Enter class time"}
                      aria-invalid={!!form.formState.errors.class_time}
                      {...form.register("class_time")}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.status}
                    errorText={
                      form.formState.errors.status?.message as
                        | string
                        | undefined
                    }
                    label="Status"
                  >
                    <select
                      {...form.register("status", {
                        setValueAs: (value) => (value === "" ? null : value),
                      })}
                      aria-invalid={!!form.formState.errors.status}
                      className="chakra-select"
                    >
                      <option value="">Select Status</option>
                      <option value="present">Present</option>
                      <option value="absent">Absent</option>
                      <option value="excused">Excused</option>
                      <option value="late">Late</option>
                      <option value="makeup">Makeup</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.check_in_time}
                    errorText={
                      form.formState.errors.check_in_time?.message as
                        | string
                        | undefined
                    }
                    label="Check In Time"
                  >
                    <Input
                      type="datetime-local"
                      placeholder={"Enter check in time"}
                      aria-invalid={!!form.formState.errors.check_in_time}
                      {...form.register("check_in_time")}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.check_out_time}
                    errorText={
                      form.formState.errors.check_out_time?.message as
                        | string
                        | undefined
                    }
                    label="Check Out Time"
                  >
                    <Input
                      type="datetime-local"
                      placeholder={"Enter check out time"}
                      aria-invalid={!!form.formState.errors.check_out_time}
                      {...form.register("check_out_time")}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.is_makeup_class}
                    errorText={
                      form.formState.errors.is_makeup_class?.message as
                        | string
                        | undefined
                    }
                    label="Is Makeup Class"
                  >
                    <Controller
                      name="is_makeup_class"
                      control={form.control}
                      render={({ field: controllerField }) => (
                        <Switch.Root
                          id="is_makeup_class"
                          checked={!!controllerField.value}
                          onCheckedChange={({ checked }) =>
                            controllerField.onChange(checked)
                          }
                          aria-label="Is Makeup Class"
                        >
                          <Switch.HiddenInput
                            ref={controllerField.ref}
                            name={controllerField.name}
                            onBlur={controllerField.onBlur}
                          />
                          <Switch.Control>
                            <Switch.Thumb />
                          </Switch.Control>
                        </Switch.Root>
                      )}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.makeup_for_date}
                    errorText={
                      form.formState.errors.makeup_for_date?.message as
                        | string
                        | undefined
                    }
                    label="Makeup For Date"
                  >
                    <Input
                      type="date"
                      placeholder={"Select makeup for date"}
                      aria-invalid={!!form.formState.errors.makeup_for_date}
                      {...form.register("makeup_for_date")}
                    />
                  </Field>

                  <Box gridColumn={{ md: "1 / -1" }}>
                    <Field
                      invalid={!!form.formState.errors.instructor_notes}
                      errorText={
                        form.formState.errors.instructor_notes?.message as
                          | string
                          | undefined
                      }
                      label="Instructor Notes"
                    >
                      <Textarea
                        {...form.register("instructor_notes")}
                        placeholder={"Enter instructor notes"}
                        rows={3}
                        resize="vertical"
                        aria-invalid={!!form.formState.errors.instructor_notes}
                      />
                    </Field>
                  </Box>

                  <Field
                    invalid={!!form.formState.errors.skills_practiced}
                    errorText={
                      form.formState.errors.skills_practiced?.message as
                        | string
                        | undefined
                    }
                    label="Skills Practiced"
                  >
                    <Input
                      placeholder={"Enter skills practiced"}
                      aria-invalid={!!form.formState.errors.skills_practiced}
                      {...form.register("skills_practiced")}
                    />
                  </Field>

                  <Box gridColumn={{ md: "1 / -1" }}>
                    <Field
                      invalid={!!form.formState.errors.behavior_notes}
                      errorText={
                        form.formState.errors.behavior_notes?.message as
                          | string
                          | undefined
                      }
                      label="Behavior Notes"
                    >
                      <Textarea
                        {...form.register("behavior_notes")}
                        placeholder={"Enter behavior notes"}
                        rows={3}
                        resize="vertical"
                        aria-invalid={!!form.formState.errors.behavior_notes}
                      />
                    </Field>
                  </Box>

                  <Field
                    invalid={!!form.formState.errors.parent_notification_sent}
                    errorText={
                      form.formState.errors.parent_notification_sent?.message as
                        | string
                        | undefined
                    }
                    label="Parent Notification Sent"
                  >
                    <Controller
                      name="parent_notification_sent"
                      control={form.control}
                      render={({ field: controllerField }) => (
                        <Switch.Root
                          id="parent_notification_sent"
                          checked={!!controllerField.value}
                          onCheckedChange={({ checked }) =>
                            controllerField.onChange(checked)
                          }
                          aria-label="Parent Notification Sent"
                        >
                          <Switch.HiddenInput
                            ref={controllerField.ref}
                            name={controllerField.name}
                            onBlur={controllerField.onBlur}
                          />
                          <Switch.Control>
                            <Switch.Thumb />
                          </Switch.Control>
                        </Switch.Root>
                      )}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.marked_by_user_id}
                    errorText={
                      form.formState.errors.marked_by_user_id?.message as
                        | string
                        | undefined
                    }
                    label="Marked By User Id"
                  >
                    <Input
                      placeholder={"Enter marked by user id"}
                      aria-invalid={!!form.formState.errors.marked_by_user_id}
                      {...form.register("marked_by_user_id")}
                    />
                  </Field>

                  <Box gridColumn={{ md: "1 / -1" }}>
                    <Field
                      invalid={!!form.formState.errors.notes}
                      errorText={
                        form.formState.errors.notes?.message as string | undefined
                      }
                      label="Notes"
                    >
                      <Textarea
                        {...form.register("notes")}
                        placeholder={"Enter notes"}
                        rows={3}
                        resize="vertical"
                        aria-invalid={!!form.formState.errors.notes}
                      />
                    </Field>
                  </Box>
                </Box>
              </DialogBody>

              <DialogFooter>
                <HStack gap={3}>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    {cancelLabel}
                  </Button>
                  <Button
                    type="submit"
                    colorPalette="blue"
                    loading={isSubmitting}
                    loadingText={loadingText ?? `${submitLabel}...`}
                  >
                    {submitLabel}
                  </Button>
                </HStack>
              </DialogFooter>
            </DialogContent>
          </DialogPositioner>
        </Portal>
      </DialogRoot>
    )
  },
)

AttendanceFormDialog.displayName = "AttendanceFormDialog"

export default AttendanceFormDialog
